import type { PronunciationDictionaryConfig } from '@circuitry-ai/doc-data';
import { sanitizePronunciationDictionaryForApi } from '@circuitry-ai/doc-data';
import {
  buildBuiltInToolsDeltaPatch,
  parseVoiceAgentBuiltInToolsState,
  type VoiceAgentBuiltInToolsState,
} from './voiceAgentToolsUtils';
import {
  sanitizeVariablesForApi,
  type VoiceAgentVariablesFormShape,
} from './voiceAgentVariablesUtils';

/** Whitelist voice-agent PATCH/POST nested objects so we do not echo read-only API fields. */

export interface FirstMessageFormShape {
  mode?: string;
  text?: string;
}

export interface LlmFormShape {
  provider?: string;
  model?: string;
  temperature?: number;
  system_prompt_template?: string;
  system_prompt_template_outbound?: string;
}

export interface SttFormShape {
  provider?: string;
  /** Mirror of live_options.model for providers that return top-level `model` from the API. */
  model?: string;
  live_options?: { model?: string; language?: string };
}

export interface TtsFormShape {
  provider?: string;
  voice_id?: string;
  model_id?: string;
  model?: string;
  default_language?: string;
  emotion?: string;
  volume?: number;
  speed?: number;
}

const TTS_VOLUME_DEFAULT = 1;
const TTS_SPEED_DEFAULT = 1;

function ttsNumericOrDefault(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function appendTtsVoiceControls(out: Record<string, unknown>, tts: TtsFormShape): void {
  if (trimmedNonEmpty(tts.emotion)) {
    out.emotion = String(tts.emotion).trim().toLowerCase();
  }
  out.volume = ttsNumericOrDefault(tts.volume, TTS_VOLUME_DEFAULT);
  out.speed = ttsNumericOrDefault(tts.speed, TTS_SPEED_DEFAULT);
}

export interface FillerWordsFormShape {
  enabled?: boolean;
  filler_phrases?: string[];
  delay_seconds?: number;
  sllm_config?: {
    provider?: string;
    model?: string;
    temperature?: number;
    max_completion_tokens?: number;
    system_prompt?: string;
  };
}

export interface IdleTimeoutFormShape {
  enabled?: boolean;
  timeout_seconds?: number;
  retries?: number;
  prompt_templates?: string[];
}

export interface KrispFormShape {
  enabled?: boolean;
  noise_suppression_level?: number;
}

export interface SummarizationFormShape {
  provider?: string;
  model?: string;
  prompt_template?: string;
}

export interface VadFormShape {
  enabled?: boolean;
  confidence?: number;
  start_secs?: number;
  stop_secs?: number;
  min_volume?: number;
  preset?: string;
  auto_adjust_for_turn_detection?: boolean;
}

/** wait_for_user: `mode` only. speak_first: `mode` + `text`. */
export function sanitizeFirstMessageForApi(fm: FirstMessageFormShape | undefined): Record<string, string> | undefined {
  const mode = fm?.mode;
  if (!mode) return undefined;
  if (mode === 'wait_for_user') {
    return { mode: 'wait_for_user' };
  }
  if (mode === 'speak_first') {
    return { mode: 'speak_first', text: fm.text ?? '' };
  }
  return undefined;
}

function trimmedNonEmpty(s: string | undefined | null): boolean {
  return typeof s === 'string' && s.trim().length > 0;
}

export function sanitizeLlmForApi(llm: LlmFormShape | undefined): Record<string, unknown> | undefined {
  if (!llm) return undefined;
  const out: Record<string, unknown> = {};
  if (llm.provider != null && llm.provider !== '') out.provider = llm.provider;
  if (llm.model != null && llm.model !== '') out.model = llm.model;
  if (llm.temperature != null && Number.isFinite(llm.temperature)) out.temperature = llm.temperature;
  const spIn = llm.system_prompt_template;
  if (trimmedNonEmpty(spIn)) out.system_prompt_template = String(spIn).trim();
  const spOut = llm.system_prompt_template_outbound;
  if (trimmedNonEmpty(spOut)) out.system_prompt_template_outbound = String(spOut).trim();
  return Object.keys(out).length ? out : undefined;
}

function unifiedSttModelFromForm(stt: SttFormShape): string | undefined {
  const m = stt.live_options?.model ?? stt.model;
  if (m == null || String(m).trim() === '') return undefined;
  return String(m).trim();
}

function unifiedSttLanguageFromForm(stt: SttFormShape): string | undefined {
  const lang = stt.live_options?.language;
  if (lang == null || String(lang).trim() === '') return undefined;
  return String(lang).trim();
}

/** Deepgram uses `live_options`; OpenAI, Google, ElevenLabs, Cartesia, Soniox use top-level `model` (OpenAPI). */
export function sanitizeSttForApi(stt: SttFormShape | undefined): Record<string, unknown> | undefined {
  if (!stt?.provider) return undefined;
  const provider = stt.provider;
  const model = unifiedSttModelFromForm(stt);
  const language = unifiedSttLanguageFromForm(stt);

  if (provider === 'deepgram') {
    const live: Record<string, unknown> = {};
    if (model) live.model = model;
    if (language) live.language = language;
    return {
      provider,
      ...(Object.keys(live).length ? { live_options: live } : {}),
    };
  }

  const out: Record<string, unknown> = { provider };
  if (model) out.model = model;
  if (language) out.language = language;
  return out;
}

/**
 * Maps UI `tts` to provider-specific JSON (AgentConfig OpenAPI uses `additionalProperties: false`
 * per variant — e.g. Sarvam uses `model`, ElevenLabs uses `model_id`; OpenAI/Azure use `voice` not `voice_id`).
 */
export function sanitizeTtsForApi(tts: TtsFormShape | undefined): Record<string, unknown> | undefined {
  if (!tts?.provider) return undefined;
  if (tts.provider === 'dynamic') {
    const out: Record<string, unknown> = { provider: 'dynamic' };
    if (trimmedNonEmpty(tts.default_language)) out.default_language = String(tts.default_language).trim();
    appendTtsVoiceControls(out, tts);
    return out;
  }

  const provider = tts.provider;
  const out: Record<string, unknown> = { provider };
  const voiceFromForm = tts.voice_id;
  const voiceTrimmed =
    voiceFromForm !== undefined && voiceFromForm !== null && String(voiceFromForm).trim() !== ''
      ? String(voiceFromForm).trim()
      : '';
  const modelRaw = tts.model_id ?? tts.model;
  const modelTrimmed = trimmedNonEmpty(modelRaw) ? String(modelRaw).trim() : '';

  if (provider === 'openai') {
    if (voiceTrimmed) out.voice = voiceTrimmed;
    if (modelTrimmed) out.model = modelTrimmed;
    appendTtsVoiceControls(out, tts);
    return out;
  }
  if (provider === 'azure') {
    if (voiceTrimmed) out.voice = voiceTrimmed;
    appendTtsVoiceControls(out, tts);
    return out;
  }
  if (provider === 'elevenlabs') {
    if (voiceTrimmed) out.voice_id = voiceTrimmed;
    if (modelTrimmed) out.model_id = modelTrimmed;
    appendTtsVoiceControls(out, tts);
    return out;
  }
  if (provider === 'sarvam' || provider === 'cartesia') {
    if (voiceTrimmed) out.voice_id = voiceTrimmed;
    if (modelTrimmed) out.model = modelTrimmed;
    appendTtsVoiceControls(out, tts);
    return out;
  }
  if (voiceTrimmed) out.voice_id = voiceTrimmed;
  if (modelTrimmed) out.model = modelTrimmed;
  appendTtsVoiceControls(out, tts);
  return out;
}

/** Advance tab full filler_words object for PATCH. */
export function sanitizeFillerWordsForApi(
  fw: FillerWordsFormShape | undefined
): {
  enabled: boolean;
  filler_phrases: string[];
  delay_seconds: number;
  sllm_config?: {
    provider?: string;
    model?: string;
    temperature?: number;
    max_completion_tokens?: number;
    system_prompt?: string;
  };
} | undefined {
  if (fw == null || typeof fw.enabled !== 'boolean') return undefined;
  const out: {
    enabled: boolean;
    filler_phrases: string[];
    delay_seconds: number;
    sllm_config?: {
      provider?: string;
      model?: string;
      temperature?: number;
      max_completion_tokens?: number;
      system_prompt?: string;
    };
  } = {
    enabled: fw.enabled,
    filler_phrases: Array.isArray(fw.filler_phrases) ? fw.filler_phrases : [],
    delay_seconds: clampNumber(fw.delay_seconds, 0, 4, 0.7),
  };
  const sllm = fw.sllm_config;
  if (sllm) {
    const config: NonNullable<typeof out.sllm_config> = {};
    if (trimmedNonEmpty(sllm.provider)) config.provider = String(sllm.provider).trim();
    if (trimmedNonEmpty(sllm.model)) config.model = String(sllm.model).trim();
    if (typeof sllm.temperature === 'number' && Number.isFinite(sllm.temperature)) {
      config.temperature = clampNumber(sllm.temperature, 0, 1, 0.7);
    }
    if (typeof sllm.max_completion_tokens === 'number' && Number.isFinite(sllm.max_completion_tokens)) {
      config.max_completion_tokens = Math.max(0, Math.round(sllm.max_completion_tokens));
    }
    if (sllm.system_prompt !== undefined) {
      config.system_prompt = String(sllm.system_prompt);
    }
    if (Object.keys(config).length > 0) out.sllm_config = config;
  }
  return out;
}

function clampInteger(value: number | undefined, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

function clampNumber(value: number | undefined, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

export function sanitizeIdleTimeoutForApi(
  idleTimeout: IdleTimeoutFormShape | undefined
): {
  enabled: boolean;
  timeout_seconds: number;
  retries: number;
  prompt_templates: string[];
} | undefined {
  if (!idleTimeout || typeof idleTimeout.enabled !== 'boolean') return undefined;
  return {
    enabled: idleTimeout.enabled,
    timeout_seconds: clampInteger(idleTimeout.timeout_seconds, 1, 30, 10),
    retries: clampInteger(idleTimeout.retries, 1, 3, 1),
    prompt_templates: Array.isArray(idleTimeout.prompt_templates)
      ? idleTimeout.prompt_templates.slice(0, 3)
      : [],
  };
}

export function sanitizeKrispForApi(
  krisp: KrispFormShape | undefined
): { enabled: boolean; noise_suppression_level: number } | undefined {
  if (!krisp || typeof krisp.enabled !== 'boolean') return undefined;
  return {
    enabled: krisp.enabled,
    noise_suppression_level: clampInteger(krisp.noise_suppression_level, 0, 100, 50),
  };
}

export function sanitizeSummarizationForApi(
  summarization: SummarizationFormShape | undefined
): { provider?: string; model?: string; prompt_template?: string } | undefined {
  if (!summarization) return undefined;
  const out: { provider?: string; model?: string; prompt_template?: string } = {};
  if (trimmedNonEmpty(summarization.provider)) out.provider = String(summarization.provider).trim();
  if (trimmedNonEmpty(summarization.model)) out.model = String(summarization.model).trim();
  if (summarization.prompt_template !== undefined) {
    out.prompt_template = String(summarization.prompt_template);
  }
  return Object.keys(out).length ? out : undefined;
}

export function sanitizeVadForApi(
  vad: VadFormShape | undefined
): {
  enabled: boolean;
  confidence: number;
  start_secs: number;
  stop_secs: number;
  min_volume: number;
  auto_adjust_for_turn_detection: boolean;
  preset?: string;
} | undefined {
  if (!vad || typeof vad.enabled !== 'boolean') return undefined;
  const out: {
    enabled: boolean;
    confidence: number;
    start_secs: number;
    stop_secs: number;
    min_volume: number;
    auto_adjust_for_turn_detection: boolean;
    preset?: string;
  } = {
    enabled: vad.enabled,
    confidence: clampNumber(vad.confidence, 0, 0.7, 0.7),
    start_secs: clampNumber(vad.start_secs, 0, 2, 0.2),
    stop_secs: clampNumber(vad.stop_secs, 0, 5, 0.8),
    min_volume: clampNumber(vad.min_volume, 0, 1, 0.6),
    auto_adjust_for_turn_detection: vad.auto_adjust_for_turn_detection ?? false,
  };
  if (trimmedNonEmpty(vad.preset)) out.preset = String(vad.preset).trim();
  return out;
}

export interface VoiceAgentEditPatchInput {
  llm?: LlmFormShape;
  stt?: SttFormShape;
  tts?: TtsFormShape;
  first_message?: FirstMessageFormShape;
  first_message_outbound?: FirstMessageFormShape;
  from_number?: string | null;
  idle_timeout?: IdleTimeoutFormShape;
  krisp?: KrispFormShape;
  summarization_enabled?: boolean;
  summarization?: SummarizationFormShape;
  filler_words?: FillerWordsFormShape;
  vad?: VadFormShape;
  pronunciation?: PronunciationDictionaryConfig;
  builtInTools?: VoiceAgentBuiltInToolsState;
  variables?: VoiceAgentVariablesFormShape;
}

export interface VoiceAgentPatchOptions {
  resolvedFromNumber: string | null | undefined;
  inboundFirstMessage: boolean;
  outboundFirstMessage: boolean;
}

function buildVoiceAgentPatchBodyInternal(
  values: VoiceAgentEditPatchInput,
  options: VoiceAgentPatchOptions
): Record<string, unknown> {
  const { resolvedFromNumber, inboundFirstMessage, outboundFirstMessage } = options;
  const body: Record<string, unknown> = {};
  const llm = sanitizeLlmForApi(values.llm);
  if (llm) body.llm = llm;
  const stt = sanitizeSttForApi(values.stt);
  if (stt) body.stt = stt;
  const tts = sanitizeTtsForApi(values.tts);
  if (tts) body.tts = tts;
  if (inboundFirstMessage) {
    const fm = sanitizeFirstMessageForApi(values.first_message);
    if (fm) body.first_message = fm;
  }
  if (outboundFirstMessage) {
    const fo = sanitizeFirstMessageForApi(values.first_message_outbound);
    if (fo) body.first_message_outbound = fo;
  }
  if (resolvedFromNumber !== undefined) body.from_number = resolvedFromNumber;
  const idleTimeout = sanitizeIdleTimeoutForApi(values.idle_timeout);
  if (idleTimeout) body.idle_timeout = idleTimeout;
  const krisp = sanitizeKrispForApi(values.krisp);
  if (krisp) body.krisp = krisp;
  if (typeof values.summarization_enabled === 'boolean') {
    body.summarization_enabled = values.summarization_enabled;
  }
  if (values.summarization_enabled) {
    const summarization = sanitizeSummarizationForApi(values.summarization);
    if (summarization) body.summarization = summarization;
  }
  const fw = sanitizeFillerWordsForApi(values.filler_words);
  if (fw) body.filler_words = fw;
  const vad = sanitizeVadForApi(values.vad);
  if (vad) body.vad = vad;
  if (values.pronunciation !== undefined) {
    body.pronunciation =
      sanitizePronunciationDictionaryForApi(values.pronunciation) ?? {
        enabled: false,
        dictionary_id: null,
      };
  }
  const variables = sanitizeVariablesForApi(values.variables);
  if (variables !== undefined) {
    body.variables = variables;
  }
  return body;
}

export function buildVoiceAgentPatchBody(
  values: VoiceAgentEditPatchInput,
  options: VoiceAgentPatchOptions
): Record<string, unknown> {
  return {
    ...buildVoiceAgentPatchBodyInternal(values, options),
    sub_type: 'Voice Agent',
  };
}

export function buildVoiceAgentDeltaPatchBody(
  current: VoiceAgentEditPatchInput,
  previous: VoiceAgentEditPatchInput,
  currentOptions: VoiceAgentPatchOptions,
  previousOptions: VoiceAgentPatchOptions
): Record<string, unknown> {
  const currentBody = buildVoiceAgentPatchBodyInternal(current, currentOptions);
  const previousBody = buildVoiceAgentPatchBodyInternal(previous, previousOptions);
  const delta: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(currentBody)) {
    const prevValue = previousBody[key];
    if (JSON.stringify(value) !== JSON.stringify(prevValue)) {
      delta[key] = value;
    }
  }

  const defaultTools = parseVoiceAgentBuiltInToolsState(undefined);
  const toolsDelta = buildBuiltInToolsDeltaPatch(
    current.builtInTools ?? defaultTools,
    previous.builtInTools ?? defaultTools
  );
  if (toolsDelta.tools) {
    delta.tools = toolsDelta.tools;
  }

  return delta;
}
